import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "./kpiFormat";

const EMPTY = { name: "", campaign_ids: "", purchase_window_days: "", intro_days: "", rollover_days: "" };

const OfferForm = ({ onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    try {
      const num = (v) => (v === "" ? null : Number(v));
      await api.post("/kpi/offers", { name: f.name, campaign_ids: f.campaign_ids.split(",").map((x) => x.trim()).filter(Boolean),
        purchase_window_days: num(f.purchase_window_days), intro_days: num(f.intro_days), rollover_days: num(f.rollover_days) });
      setF(EMPTY);
      toast.success("Offer added");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-2 md:grid-cols-[1.4fr_1.4fr_repeat(3,0.7fr)_auto]" data-testid="kpi-offer-form">
      <Input value={f.name} onChange={set("name")} placeholder="Offer name" data-testid="kpi-offer-name" />
      <Input value={f.campaign_ids} onChange={set("campaign_ids")} placeholder="Meta campaign IDs (comma separated)" data-testid="kpi-offer-campaigns" />
      <Input type="number" value={f.purchase_window_days} onChange={set("purchase_window_days")} placeholder="Buy within" data-testid="kpi-offer-window" />
      <Input type="number" value={f.intro_days} onChange={set("intro_days")} placeholder="Intro days" data-testid="kpi-offer-intro" />
      <Input type="number" value={f.rollover_days} onChange={set("rollover_days")} placeholder="Rollover" data-testid="kpi-offer-rollover" />
      <Button size="sm" onClick={save} disabled={!f.name.trim()} data-testid="kpi-offer-add-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
    </div>
  );
};

export const OffersPanel = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/kpi/offers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const archive = async (o) => {
    try { await api.put(`/kpi/offers/${o.id}`, { archived: true }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="kpi-offers-panel">
      <div>
        <h3 className="font-display text-lg font-semibold">Campaign offers</h3>
        <p className="text-sm text-zinc-500">Link Meta campaigns to an offer so its own purchase window is used for conversion (instead of one fixed window).</p>
      </div>
      {rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="kpi-offers-empty">No offers yet — the default windows in the rules apply.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-2xs uppercase tracking-[0.18em] text-zinc-500">
            <th className="pb-2">Offer</th><th className="pb-2">Campaigns</th><th className="pb-2">Buy within</th><th className="pb-2">Intro</th><th className="pb-2">Rollover</th><th /></tr></thead>
          <tbody>{rows.map((o) => (
            <tr key={o.id} className="border-t border-zinc-100" data-testid={`kpi-offer-row-${o.id}`}>
              <td className="py-2">{o.name}</td><td className="py-2 font-mono-num text-xs">{(o.campaign_ids || []).join(", ") || "—"}</td>
              <td className="py-2 font-mono-num">{o.purchase_window_days ?? "default"}</td><td className="py-2 font-mono-num">{o.intro_days ?? "—"}</td>
              <td className="py-2 font-mono-num">{o.rollover_days ?? "—"}</td>
              <td className="py-2 text-right">{canEdit && <Button size="sm" variant="ghost" onClick={() => archive(o)} data-testid={`kpi-offer-archive-${o.id}`}>Archive</Button>}</td>
            </tr>))}</tbody>
        </table>
      )}
      {canEdit && <OfferForm onSaved={load} />}
    </section>
  );
};

export const AuditPanel = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/kpi/audit", { params: { limit: 60 } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-3" data-testid="kpi-audit-panel">
      <h3 className="font-display text-lg font-semibold">Audit trail</h3>
      {!rows ? <div className="h-20 animate-pulse rounded bg-zinc-100" /> : (
        <div className="max-h-80 overflow-y-auto text-xs space-y-1">
          {rows.map((a) => (
            <div key={a.id} className="flex gap-2 border-b border-zinc-50 py-1">
              <span className="font-mono-num text-zinc-400 shrink-0">{fmtDateTime(a.at)}</span>
              <span className="text-zinc-800 font-medium">{a.action}</span>
              <span className="text-zinc-500">{a.actor_name}{a.week ? ` · week ${a.week}` : ""}{a.reason ? ` · ${a.reason}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
